import {Injectable} from '@angular/core';
import {EditorRegistryService} from 'src/app/diagram-render/mx-graph/editor/editor-registry.service';
import {TextEditingStylesService} from 'src/app/diagram-render/mx-graph/graph/text-editing-styles.service';

declare const mxClient;

@Injectable({
  providedIn: 'root'
})
export class TextFormattingService {
  constructor(
    private editorRegistry: EditorRegistryService,
    private textEditingStylesService: TextEditingStylesService
  ) {}

  private readonly commandElementDictionary: Record<string, string> = {
    insertUnorderedList: 'ul',
    insertOrderedList: 'ol'
  };

  private readonly graph = this.editorRegistry.getEditorUI().editor.graph;

  private readonly isIE = mxClient.IS_IE || mxClient.IS_IE11;

  setFontColor(color: string) {
    document.execCommand('forecolor', false, color);
  }

  setFontStyle(event) {
    let value;

    switch (event.param) {
      case 'fontBold':
        value = 'bold';
        break;

      case 'fontItalic':
        value = 'italic';
        break;

      case 'fontUnderline':
        value = 'underline';
        break;

      case 'fontSize':
        this.setFontSize(event.val);

        return;

      case 'font':
        this.setFontFamily(event.val);

        return;
    }

    document.execCommand(value);
  }

  setFontFamily(name: string) {
    document.execCommand('fontName', false, name);
  }

  setFontSize(fontSize: string) {
    // Creates an element with arbitrary size 2
    document.execCommand('selectAll');
    document.execCommand('fontSize', false, '2');

    // Changes the css font size of the first font element inside the in-place editor with size 2
    const elts = this.graph.cellEditor.text2.getElementsByTagName('font');

    for (let i = 0; i < elts.length; i++) {
      if (elts[i].getAttribute('size') === '2') {
        elts[i].removeAttribute('size');
        elts[i].style.fontSize = parseInt(fontSize, 10) + 'px';
      }
    }

    this.refreshEditingAreaFontSize(fontSize);
  }

  private refreshEditingAreaFontSize(fontSize: string) {
    const editArea = this.graph.cellEditor.text2;

    if (editArea) {
      editArea.style.fontSize = parseInt(fontSize, 10) * this.graph.view.scale + 'px';
    }
  }

  private stringToBoolean(str: string): boolean {
    return str === 'true';
  }

  private justifyText(listCommand: string): void {
    const isListApplied = this.stringToBoolean(this.selInfo(listCommand));

    if (isListApplied) {
      const elementToFind = this.commandElementDictionary[listCommand];

      if (!elementToFind) {
        return;
      }

      const parentElement = document.getSelection().anchorNode.parentNode as HTMLElement;
      const closestList = parentElement?.closest(elementToFind) as
        | HTMLOListElement
        | HTMLUListElement;

      if (closestList) {
        closestList.style.textAlign = 'left';
      }
    }
  }

  private toggleList(listCommand: string): void {
    document.execCommand(listCommand);
    this.justifyText(listCommand);
  }

  public toggleUnorderedList(): void {
    this.toggleList('insertUnorderedList');
  }

  public toggleOrderedList(): void {
    this.toggleList('insertOrderedList');
  }

  _refreshFontInfo(shapeJson) {
    const fontName = this.selInfo('fontName');

    shapeJson.style.font = fontName ? fontName.replace(/['"]+/g, '') : '';
    shapeJson.style.fontSize =
      this.textEditingStylesService.fontSizeTag?.style?.fontSize ||
      this.textEditingStylesService.defaultFontSize;
    shapeJson.style.fontColor = this._getCurrentColor();
    this.refreshEditingAreaFontSize(shapeJson.style.fontSize);
  }

  private _getCurrentColor() {
    const color: any = this.selInfo('forecolor');

    if (this.isIE) {
      /* tslint:disable */
      const i = {
        r: color & 0xff,
        g: (color >> 8) & 0xff,
        b: (color >> 16) & 0xff
      };
      /* tslint:enable */
      const result = [i.r, i.g, i.b].join(', ');

      return this.rgbToHex('rgb(' + result + ')');
    }

    if (!color) {
      return '#000000';
    }

    return this.rgbToHex(color);
  }

  rgbToHex(color: string) {
    if (color.substr(0, 1) === '#') {
      return color;
    }

    const digits =
      /(.*?)rgb\((\d+), (\d+), (\d+)\)/.exec(color) ||
      /(.*?)rgba\((\d+), (\d+), (\d+), (\d+\.?\d*)\)/.exec(color);

    if (!digits) {
      return '#000000';
    }

    const red = this.addZeroIfNeeded(parseInt(digits[2], 10).toString(16));
    const green = this.addZeroIfNeeded(parseInt(digits[3], 10).toString(16));
    const blue = this.addZeroIfNeeded(parseInt(digits[4], 10).toString(16));

    return ('#' + red + green + blue).toUpperCase();
  }

  addZeroIfNeeded(color: string) {
    if (color.length === 1) {
      return '0' + color;
    }

    return color;
  }

  private _getFontSize() {
    let containerEl, sel;

    if (window.getSelection) {
      sel = window.getSelection();

      if (sel.rangeCount) {
        containerEl = sel.getRangeAt(0).commonAncestorContainer;

        if (containerEl.nodeType === 3) {
          containerEl = containerEl.parentNode;
        }
      }
    } else if ((sel = document.getSelection) && sel.type !== 'Control') {
      containerEl = sel.createRange().parentElement();
    }

    return Math.round(parseInt(this._getComputedStyleProperty(containerEl, 'fontSize'), 10));
  }

  private _getComputedStyleProperty(el, propName) {
    if (window.getComputedStyle && el) {
      return window.getComputedStyle(el, null)[propName];
    } else if (el && el.currentStyle) {
      return el.currentStyle[propName];
    } else {
      return 0;
    }
  }

  selInfo(str: string) {
    return document.queryCommandValue(str);
  }

  get buttonsEnabled() {
    return {
      bold: document.queryCommandState('bold'),
      italic: document.queryCommandState('italic'),
      underline: document.queryCommandState('underline')
    };
  }
}
